// Pure self-contained HTML sanitizer without external module dependencies
export function sanitizeExternalDescription(rawHtml: any): string {
  if (!rawHtml) return '';
  
  // 1. Remove GDPR boilerplate lines, contact protected strings, and recruiter phone/emails
  let cleaned = String(rawHtml);
  cleaned = cleaned.replace(/<p>\s*<em>\s*GDPR\s+Compliance:[\s\S]*?<\/em>\s*<\/p>/gi, '');
  cleaned = cleaned.replace(/GDPR Compliance:[\s\S]*?(?:\[Contact Info Protected\]|$)/gi, '');
  cleaned = cleaned.replace(/\[Contact Info Protected\]/gi, '');
  cleaned = cleaned.replace(/\[Link Protected\]/gi, '');
  cleaned = cleaned.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi, '');
  cleaned = cleaned.replace(/(?:\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4,9}/g, '');

  // 2. Strip scripts, styles, objects, embeds, iframes
  cleaned = cleaned.replace(/<(script|style|iframe|object|embed|svg|canvas)[^>]*>[\s\S]*?<\/\1>/gi, '');
  cleaned = cleaned.replace(/<(script|style|iframe|object|embed|svg|canvas)[^>]*\/?>/gi, '');

  // 3. Strip inline style attributes and event handlers (onclick, onerror, onload, etc.)
  cleaned = cleaned.replace(/\s+(?:style|on[a-z]+)=["'][^"']*["']/gi, '');

  // 4. Strip any tags except allowed ones: p, br, ul, ol, li, strong, em, b, i, h3, h4, a
  cleaned = cleaned.replace(/<(?!\/?(?:p|br|ul|ol|li|strong|em|b|i|h3|h4|a)\b)[^>]+>/gi, '');

  // 5. Ensure safe anchors with target="_blank" rel="nofollow noopener"
  cleaned = cleaned.replace(/<a\s+[^>]*href=["']([^"']*)["'][^>]*>/gi, '<a href="$1" target="_blank" rel="nofollow noopener">');

  return cleaned.trim();
}

/**
 * Extracts a clean plain-text excerpt of ~160 characters with all HTML tags stripped.
 */
export function extractPlainTextExcerpt(htmlOrText: any, maxLength = 160): string {
  if (!htmlOrText) return '';
  let text = String(htmlOrText);
  
  // 1. Remove GDPR boilerplate lines & protected markers
  text = text.replace(/<p>\s*<em>\s*GDPR\s+Compliance:[\s\S]*?<\/em>\s*<\/p>/gi, '');
  text = text.replace(/GDPR Compliance:[\s\S]*?(?:\[Contact Info Protected\]|$)/gi, '');
  text = text.replace(/\[(?:Contact\s+Info\s+Protected|Link\s+Protected|Email\s+Protected|Phone\s+Protected)\]/gi, '');
  
  // 2. Decode common HTML entities (both single and double encoded)
  text = text
    .replace(/&amp;lt;/gi, '<')
    .replace(/&amp;gt;/gi, '>')
    .replace(/&amp;quot;/gi, '"')
    .replace(/&amp;amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&nbsp;/gi, ' ');

  // 3. Strip all HTML tags
  text = text.replace(/<[^>]+>/g, ' ');

  // 4. Secondary pass for any stubborn encoded tags like &lt;b&gt;
  text = text.replace(/&lt;[^&]+&gt;/gi, ' ').replace(/<[^>]+>/g, ' ');

  // 5. Clean whitespace
  text = text.replace(/\s+/g, ' ').trim();
  
  if (text.length <= maxLength) return text;
  
  // Truncate at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.7) {
    return truncated.substring(0, lastSpace) + '...';
  }
  return truncated + '...';
}

/**
 * Parses real hiring company and cleaned job title.
 * For WWR: parses "Company: Role" or "Company - Role".
 * Rejects "... Client", "Himalayas Client", "WWR Client" or placeholder names.
 */
export function parseRealCompany(rawCompany, rawTitle, source) {
  let company = (rawCompany || '').trim();
  let title = (rawTitle || '').trim();

  // Special handling for We Work Remotely
  if (source === 'We Work Remotely' || source === 'WWR') {
    if (title.includes(':')) {
      const parts = title.split(':');
      const candidateCompany = parts[0].trim();
      if (candidateCompany && candidateCompany.length > 1) {
        company = candidateCompany;
        title = parts.slice(1).join(':').trim();
      }
    }
  }

  // Reject placeholder companies
  const lowerCompany = company.toLowerCase();
  if (
    !company ||
    lowerCompany === 'wwr client' ||
    lowerCompany === 'himalayas client' ||
    lowerCompany === 'jobicy client' ||
    lowerCompany === 'client' ||
    lowerCompany === 'unknown' ||
    lowerCompany.endsWith(' client') ||
    lowerCompany.endsWith(' member') ||
    lowerCompany.includes('feeder') ||
    lowerCompany.includes('aggregator')
  ) {
    return { valid: false, company: '', title, reason: `Placeholder or missing company: "${rawCompany}"` };
  }

  return { valid: true, company, title };
}

/**
 * Extracts technical skills strictly present in the title or description text.
 * No default or random skills added.
 */
const CANONICAL_TECH_SKILLS = [
  'React', 'TypeScript', 'JavaScript', 'Node.js', 'Python', 'Go', 'Golang',
  'Rust', 'Ruby', 'Rails', 'Ruby on Rails', 'Java', 'Kotlin', 'Swift',
  'Flutter', 'React Native', 'Vue', 'Angular', 'Next.js', 'Docker',
  'Kubernetes', 'AWS', 'GCP', 'Azure', 'Terraform', 'PostgreSQL', 'MySQL',
  'MongoDB', 'GraphQL', 'REST API', 'TailwindCSS', 'CSS', 'HTML', 'Solidity',
  'C++', 'C#', '.NET', 'Django', 'FastAPI', 'Spring Boot', 'DevOps', 'CI/CD',
  'Linux', 'Redis', 'Kafka', 'Elasticsearch', 'Machine Learning', 'PyTorch',
  'TensorFlow', 'LLMs', 'OpenAI', 'SRE', 'SDET', 'QA Automation', 'Selenium',
  'Cypress', 'Playwright', 'SQL', 'NoSQL'
];

export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function extractSkillsStrict(title: string, description: string): string[] {
  const combined = `${title || ''} ${description || ''}`;
  const matched: string[] = [];

  for (const skill of CANONICAL_TECH_SKILLS) {
    const escaped = escapeRegex(skill);
    const startBound = /^\w/.test(skill) ? '\\b' : '(?<=\\s|^|[([{"\'])';
    const endBound = /\w$/.test(skill) ? '\\b' : '(?=\\s|[.,;:!?)\\]}"\']|$)';
    const regex = new RegExp(`${startBound}${escaped}${endBound}`, 'i');
    if (regex.test(combined)) {
      matched.push(skill);
    }
  }

  return matched;
}

/**
 * Formats external salary ONLY when amount, currency AND period are provided.
 * Otherwise returns 'Not specified'.
 */
export function formatExternalSalary(amount, currency, period) {
  if (!amount || !currency || !period) {
    return 'Not specified';
  }

  const cleanPeriod = String(period).toLowerCase().trim();
  const normalizedPeriod = cleanPeriod.includes('hour') ? 'hour' : (cleanPeriod.includes('month') ? 'month' : (cleanPeriod.includes('year') ? 'year' : null));
  if (!normalizedPeriod) {
    return 'Not specified';
  }

  const cleanCurrency = String(currency).toUpperCase().trim();
  let formattedAmount = '';
  if (typeof amount === 'number') {
    formattedAmount = amount.toLocaleString('en-US');
  } else if (typeof amount === 'string') {
    formattedAmount = amount.trim();
  } else {
    return 'Not specified';
  }

  return `${cleanCurrency} ${formattedAmount} / ${normalizedPeriod}`;
}
